/**
 * The exercises, as a child sees them.
 *
 * A handful of widgets cover every exercise type but `label_scene`, because
 * most of the types differ in what they *practise* rather than in what the
 * child does with their fingers: choosing between two things is one
 * interaction whether the prompt is a sound, a word or a picture, and typing is
 * one whether the prompt is a word, a sound or a sentence with a gap
 * ([D-149](../../../docs/DECISIONS.md#d-149)).
 *
 * **Sound comes from wherever it can.** A tap to hear a word plays the course's
 * clip ([D-146](../../../docs/DECISIONS.md#d-146)) and otherwise asks the device
 * to say it ([`say`](./audio.ts), [D-013](../../../docs/DECISIONS.md#d-013)).
 * The choice is made per word rather than per lesson, so one missing clip costs
 * one word, not a lesson.
 *
 * `audio_word_match` is the one type that stays *seen but not assessed*: its
 * authored no-audio fallback asks for pictures nobody has drawn
 * ([D-063](../../../docs/DECISIONS.md#d-063)), and a guess between two silent
 * tiles is worse than an honest listen.
 *
 * Payloads are parsed with the content package's own Zod schema rather than
 * with a copy of it, so an exercise the linter accepts is an exercise this can
 * render, and there is no second definition to drift.
 */

import '@capytalk/tokens/blocks/quiz.css';

import { ExercisePayloadSchema } from '@capytalk/content/schema';
import type { Outcome } from '@capytalk/core';
import {
  type GapsMarked,
  afterCheck,
  gapsIn,
  hintFor,
  markGaps,
  normalise,
  parseGaps,
  scoreChoice,
  scoreSpoken,
  scoreTyped,
  scoreWordBank,
  seededRandom,
  storyStep,
} from '@capytalk/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { scene } from '@capytalk/tokens';
import { assetUrl, hush, say, spokenLanguage } from './audio.js';
import { Tappable, useSayWord } from './Hint.js';
import { CORRECT, pick } from './praise.js';
import type { SpeechRecognizer } from './speech.js';
import Stage, { Portrait } from './Stage.js';

type Payload = ReturnType<typeof ExercisePayloadSchema.parse>;

/** What the child did with one exercise. */
export type Verdict =
  | {
      readonly kind: 'answered';
      readonly outcome: Outcome;
      /** 0–1. */
      readonly score: number;
      /** The right answer, when it is worth showing. */
      readonly correction: string | null;
      /**
       * Per-sound confidence, from a spoken attempt.
       *
       * Only speaking produces these, so it is absent everywhere else rather
       * than an empty object — "this exercise says nothing about
       * pronunciation" and "this child pronounced nothing well" must not look
       * the same to the gap map.
       */
      readonly phonemes?: Readonly<Record<string, number>>;
    }
  /**
   * Shown but not assessed, so **nothing is recorded**.
   *
   * A child who was never asked a question has not got one wrong, and writing a
   * `skipped` row for it would drag their word schedules and their difficulty
   * band around on evidence that does not exist.
   */
  | { readonly kind: 'seen' };

const right = (score = 1): Verdict => ({
  kind: 'answered',
  outcome: 'correct',
  score,
  correction: null,
});

/**
 * How much of a free spoken answer has to land.
 *
 * `speak_repeat` carries an authored `toleranceBand` because the target is one
 * fixed phrase. `speak_answer` has none: the child picks which acceptable
 * answer to give, and the exercise cannot know in advance how long it will be.
 * Two thirds is deliberately generous — the recogniser is the unreliable half
 * of this exchange ([D-004](../../../docs/DECISIONS.md#d-004)).
 */
export const SPOKEN_ANSWER_TOLERANCE = 2 / 3;

/** Deterministic per exercise, so a child sees the same board twice. */
function shuffled<T>(items: readonly T[], seed: string): T[] {
  const random = seededRandom(seed);
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j] as T, out[i] as T];
  }
  return out;
}

// --- hearing the word -------------------------------------------------------

/**
 * The tap that says the word out loud.
 *
 * Two taps, not one: normal and slower. *Slower* is not a nicety — a child who
 * cannot pick `hi` out of `hello` at speed usually can at half of it, and
 * [Accessibility](../../../docs/ACCESSIBILITY.md) asks for it by name.
 *
 * If nothing can be produced — no recording, and a device with no English voice
 * — the control says so rather than sitting there doing nothing when tapped.
 * The exercise around it stays answerable either way, because the word is also
 * written on the screen.
 */
function HearIt({
  audioRef,
  text,
  autoPlay = false,
}: {
  readonly audioRef: string;
  readonly text: string;
  /**
   * Say it once on arrival. For the exercises whose question *is* the sound —
   * "What do you hear?", dictation — where a silent screen with answers on it
   * is a guess, not a question. The buttons stay, so it can be heard again.
   */
  readonly autoPlay?: boolean;
}) {
  const [silent, setSilent] = useState(false);
  const played = useRef(false);

  useEffect(() => {
    if (!autoPlay || played.current) return;
    played.current = true;
    void say(audioRef, text).then((said) => {
      setSilent(said.from === 'none');
    });
  }, [autoPlay, audioRef, text]);

  // Stop mid-word when the exercise goes away, or the previous word talks over
  // the next question.
  useEffect(() => hush, []);

  const tap = (slow: boolean) => () => {
    void say(audioRef, text, { slow }).then((said) => {
      setSilent(said.from === 'none');
    });
  };

  return (
    <div
      className="row"
      style={{ justifyContent: 'center', gap: 9, marginTop: 12, flexWrap: 'wrap' }}
    >
      <button className="chip" type="button" onClick={tap(false)}>
        <span aria-hidden="true">{autoPlay ? '🔁' : '🔊'}</span>{' '}
        {autoPlay ? 'Hear it again' : 'Hear it'}
      </button>
      <button className="chip" type="button" onClick={tap(true)}>
        <span aria-hidden="true">🐢</span> Slower
      </button>
      {silent && (
        <span className="tiny" role="status">
          Sound does not work here. Read the word instead.
        </span>
      )}
    </div>
  );
}

// --- choosing one of several ------------------------------------------------

interface Choice {
  readonly key: string;
  readonly label: string;
  /** A picture to choose between, when the exercise is about pictures. */
  readonly imageRef?: string | undefined;
  readonly correct: boolean;
}

/**
 * Tap one of several.
 *
 * `listen_choose`, `meaning_choose` and `picture_choice` are this widget. The
 * prompt differs; the interaction does not.
 */
function Choose({
  exerciseId,
  prompt,
  written,
  audioRef,
  options,
  onDone,
}: {
  readonly exerciseId: string;
  readonly prompt: string;
  /** The word being asked about. Shown as well as spoken, always. */
  readonly written: string | null;
  /** The recording to prefer when the child taps to hear it. */
  readonly audioRef: string | null;
  readonly options: readonly Choice[];
  readonly onDone: (verdict: Verdict) => void;
}) {
  const order = useMemo(() => shuffled(options, exerciseId), [options, exerciseId]);
  const correct = options.find((o) => o.correct);
  const pictures = options.some((o) => o.imageRef !== undefined);

  const answer = (option: Choice) => () => {
    const scored = scoreChoice(option.key, correct?.key ?? '');
    onDone({
      kind: 'answered',
      outcome: scored.outcome,
      score: scored.outcome === 'correct' ? 1 : 0,
      correction: scored.outcome === 'correct' ? null : (correct?.label ?? null),
    });
  };

  return (
    <div className="pad" style={{ paddingTop: 18 }}>
      <p className="kicker">
        <Tappable text={prompt} />
      </p>
      {written !== null && (
        <div className="card center" style={{ marginTop: 10, padding: 20 }}>
          <div style={{ fontSize: '1.4375rem', fontWeight: 800 }}>
            <Tappable text={written} />
          </div>
          {audioRef !== null && <HearIt audioRef={audioRef} text={written} />}
        </div>
      )}
      {/*
        "What do you hear?" — the sound is the question, so it is played the
        moment the question arrives and can be played again as often as a
        child likes. It used to render nothing at all here: the only play
        button lived inside the written card, which this exercise never shows
        because the written word is the answer.
      */}
      {written === null && audioRef !== null && correct !== undefined && (
        <div className="card center" style={{ marginTop: 10, padding: 20 }}>
          <div style={{ fontSize: 40 }} aria-hidden="true">
            👂
          </div>
          <HearIt audioRef={audioRef} text={correct.label} autoPlay />
        </div>
      )}

      {pictures ? (
        <div className="grid2" style={{ marginTop: 16, gap: 10 }}>
          {order.map((option) => (
            <button
              key={option.key}
              className="tile"
              type="button"
              style={{ padding: 8 }}
              // The picture *is* the answer, so it carries the accessible name:
              // a screen reader announces the word, not "image".
              aria-label={option.label}
              onClick={answer(option)}
            >
              {option.imageRef === undefined ? (
                option.label
              ) : (
                <img
                  src={assetUrl(option.imageRef)}
                  alt=""
                  style={{ width: '100%', display: 'block', borderRadius: 10 }}
                />
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="tiles" style={{ marginTop: 16 }}>
          {order.map((option) => (
            <button
              key={option.key}
              className="tile"
              type="button"
              onClick={answer(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** An option's stable key: its authored id, or its label when it has none. */
const keyOf = (
  option: { readonly id?: string | undefined; readonly label?: string | undefined },
  at: number,
): string => option.id ?? option.label ?? `option-${String(at)}`;

// --- tap the thing (label_scene) ---------------------------------------------

interface Target {
  readonly text: string;
  readonly audioRef: string;
  /** `[x, y, width, height]` in the stage's own units (D-155). */
  readonly hitbox: readonly [number, number, number, number];
}

/**
 * Sneakers names a thing on a stage; the child taps it
 * ([D-155](../../../docs/DECISIONS.md#d-155)).
 *
 * The word is written as well as spoken, which is the scaffolding the design
 * gives this type and why it stops at the third band. A wrong tap is not just
 * wrong: it says what the child *did* tap — *"That's the table."* — so a miss
 * teaches a word too. Scored like matching pairs: no misses is `correct`, some
 * is `partial`, because finding it in the end is still finding it.
 *
 * The boxes are invisible buttons over the drawing, at least 44 points each
 * however small the thing, with the smaller ones on top so an umbrella stood in
 * the sea is the umbrella. Each is named by its word, as picture choice names
 * its pictures: a screen reader user hears the words and chooses.
 */
function LabelScene({
  exerciseId,
  stage,
  targets,
  onDone,
}: {
  readonly exerciseId: string;
  readonly stage: {
    readonly viewBox: string;
    readonly body: string;
    readonly name: string;
  };
  readonly targets: readonly Target[];
  readonly onDone: (verdict: Verdict) => void;
}) {
  const asked = useMemo(() => shuffled(targets, exerciseId), [targets, exerciseId]);
  const [at, setAt] = useState(0);
  const [found, setFound] = useState<readonly string[]>([]);
  const [misses, setMisses] = useState(0);
  const [said, setSaid] = useState<string | null>(null);

  const [, , width = 320, height = 120] = stage.viewBox.split(/\s+/).map(Number);
  // Biggest first, so the smaller boxes are drawn later and take the tap.
  const layers = useMemo(
    () =>
      [...targets].sort(
        (a, b) => b.hitbox[2] * b.hitbox[3] - a.hitbox[2] * a.hitbox[3],
      ),
    [targets],
  );

  const current = asked[at];
  if (current === undefined) return null;

  const tap = (target: Target) => () => {
    if (found.includes(target.text)) return;
    if (target.text !== current.text) {
      setMisses((n) => n + 1);
      setSaid(`That’s the ${target.text}.`);
      void say(target.audioRef, target.text);
      return;
    }
    setSaid(null);
    const done = [...found, target.text];
    setFound(done);
    if (done.length < asked.length) {
      setAt(at + 1);
      return;
    }
    onDone({
      kind: 'answered',
      outcome: misses === 0 ? 'correct' : 'partial',
      score: Math.max(0, 1 - misses / asked.length),
      correction: null,
    });
  };

  return (
    <div className="pad" style={{ paddingTop: 18 }}>
      <p className="kicker">
        <Tappable text="Tap the thing Sneakers says" />
      </p>
      <div className="card center" style={{ marginTop: 10, padding: 16 }}>
        <div style={{ fontSize: '1.4375rem', fontWeight: 800 }}>{current.text}</div>
        <HearIt audioRef={current.audioRef} text={current.text} />
      </div>
      <div
        style={{
          position: 'relative',
          marginTop: 14,
          borderRadius: 16,
          overflow: 'hidden',
          aspectRatio: `${String(width)} / ${String(height)}`,
        }}
      >
        <svg
          viewBox={stage.viewBox}
          role="img"
          aria-label={stage.name}
          style={{ width: '100%', height: '100%', display: 'block' }}
          dangerouslySetInnerHTML={{ __html: stage.body }}
        />
        {layers.map((target) => {
          const [x, y, w, h] = target.hitbox;
          const done = found.includes(target.text);
          return (
            <button
              key={target.text}
              type="button"
              aria-label={target.text}
              aria-pressed={done}
              onClick={tap(target)}
              className={done ? 'spot found' : 'spot'}
              style={{
                left: `${String(((x + w / 2) / width) * 100)}%`,
                top: `${String(((y + h / 2) / height) * 100)}%`,
                width: `max(${String((w / width) * 100)}%, 44px)`,
                height: `max(${String((h / height) * 100)}%, 44px)`,
              }}
            >
              {done && <span className="spot-word">{target.text}</span>}
            </button>
          );
        })}
      </div>
      <p className="tiny" role="status" style={{ marginTop: 8, minHeight: '1.4em' }}>
        {said ?? `${String(found.length)} of ${String(asked.length)} found`}
      </p>
    </div>
  );
}

// --- matching pairs ---------------------------------------------------------

/**
 * Tap a word, then tap what it means.
 *
 * Scored on how many wrong pairings it took: none is `correct`, some is
 * `partial`, and a child who flailed through the whole board scores nothing.
 * Getting there eventually still counts for something, because it is a matching
 * puzzle and elimination is a legitimate way to solve one.
 */
function MatchPairs({
  exerciseId,
  pairs,
  onDone,
}: {
  readonly exerciseId: string;
  readonly pairs: readonly { readonly left: string; readonly right: string }[];
  readonly onDone: (verdict: Verdict) => void;
}) {
  const rights = useMemo(
    () =>
      shuffled(
        pairs.map((p) => p.right),
        exerciseId,
      ),
    [pairs, exerciseId],
  );
  const [picked, setPicked] = useState<string | null>(null);
  const [matched, setMatched] = useState<readonly string[]>([]);
  const [wrong, setWrong] = useState(0);
  const [missed, setMissed] = useState(false);

  const settle = (left: string, rightSide: string): void => {
    const isPair = pairs.some((p) => p.left === left && p.right === rightSide);
    setPicked(null);

    if (!isPair) {
      setWrong((n) => n + 1);
      setMissed(true);
      return;
    }

    setMissed(false);
    const done = [...matched, left];
    setMatched(done);
    if (done.length < pairs.length) return;

    const score = Math.max(0, 1 - wrong / pairs.length);
    onDone({
      kind: 'answered',
      outcome: wrong === 0 ? 'correct' : 'partial',
      score,
      correction: null,
    });
  };

  return (
    <div className="pad" style={{ paddingTop: 18 }}>
      <p className="kicker">
        <Tappable text="Match the words" />
      </p>
      <div className="grid2" style={{ marginTop: 12, gap: 10 }}>
        <div className="tiles" style={{ flexDirection: 'column' }}>
          {pairs.map((pair) => (
            <button
              key={pair.left}
              type="button"
              className={`tile${matched.includes(pair.left) ? ' used' : picked === pair.left ? ' sel' : ''}`}
              disabled={matched.includes(pair.left)}
              onClick={() => {
                setPicked(pair.left);
              }}
            >
              {pair.left}
            </button>
          ))}
        </div>
        <div className="tiles" style={{ flexDirection: 'column' }}>
          {rights.map((rightSide) => {
            const owner = pairs.find((p) => p.right === rightSide)?.left ?? '';
            const taken = matched.includes(owner);
            return (
              <button
                key={rightSide}
                type="button"
                className={`tile${taken ? ' used' : ''}`}
                disabled={taken || picked === null}
                onClick={() => {
                  if (picked !== null) settle(picked, rightSide);
                }}
              >
                {rightSide}
              </button>
            );
          })}
        </div>
      </div>
      {missed && (
        <p className="sub" role="status" style={{ marginTop: 12 }}>
          Not quite. Try another one.
        </p>
      )}
    </div>
  );
}

// --- putting things in order ------------------------------------------------

/**
 * Build a line out of tiles.
 *
 * `word_bank_order` and `order_dialogue` are the same interaction over different
 * units — words in a sentence, lines in a conversation — and both are scored by
 * `scoreWordBank`, which accepts every ordering the author marked as reading
 * correctly.
 */
function PutInOrder({
  exerciseId,
  prompt,
  tiles,
  accepted,
  hear,
  imageRef,
  onDone,
}: {
  readonly exerciseId: string;
  readonly prompt: string;
  readonly tiles: readonly string[];
  /** Every ordering that counts as right. The first is the one shown as the answer. */
  readonly accepted: readonly (readonly string[])[];
  /**
   * A sound to build from, for dictation: the sentence is heard, never shown,
   * because it is the answer.
   */
  readonly hear?: { readonly audioRef: string; readonly text: string } | undefined;
  /** A picture to describe, for `build_sentence`. */
  readonly imageRef?: string | undefined;
  readonly onDone: (verdict: Verdict) => void;
}) {
  const bank = useMemo(() => shuffled(tiles, exerciseId), [tiles, exerciseId]);
  // Indices into `bank`, so two identical words are two separate tiles.
  const [built, setBuilt] = useState<readonly number[]>([]);
  // Every tile says itself when tapped, so a child who cannot read a word yet
  // can still hear which one it is.
  const sayWord = useSayWord(bank);
  useEffect(() => hush, []);

  return (
    <div className="pad" style={{ paddingTop: 18 }}>
      <p className="kicker">
        <Tappable text={prompt} />
      </p>
      {imageRef !== undefined && (
        <img
          src={assetUrl(imageRef)}
          alt=""
          style={{
            display: 'block',
            width: 140,
            margin: '12px auto 0',
            borderRadius: 14,
          }}
        />
      )}
      {hear !== undefined && (
        <HearIt audioRef={hear.audioRef} text={hear.text} autoPlay />
      )}
      {/* A group, because a label on a plain div is not allowed and says
          nothing to a screen reader — the word-bank screens had this until
          axe was pointed at one. */}
      <div
        className="slot"
        role="group"
        aria-label="Your sentence"
        style={{ marginTop: 12 }}
      >
        {built.map((at) => (
          <button
            key={at}
            className="tile"
            type="button"
            onClick={() => {
              setBuilt(built.filter((i) => i !== at));
            }}
          >
            {bank[at]}
          </button>
        ))}
      </div>
      {/*
        Hear what you have built so far, read back as one line — the same
        "listen to it again" every other spoken thing on screen has.
      */}
      {built.length > 1 && (
        <div className="row" style={{ justifyContent: 'center', marginTop: 8 }}>
          <button
            className="chip"
            type="button"
            onClick={() => {
              void say(null, built.map((at) => bank[at] ?? '').join(' '));
            }}
          >
            <span aria-hidden="true">🔁</span> Hear my sentence
          </button>
        </div>
      )}
      <div className="tiles" style={{ marginTop: 14 }}>
        {bank.map((word, at) => (
          <button
            key={`${word}-${String(at)}`}
            type="button"
            className={`tile${built.includes(at) ? ' used' : ''}`}
            disabled={built.includes(at)}
            onClick={() => {
              setBuilt([...built, at]);
              sayWord(word);
            }}
          >
            {word}
          </button>
        ))}
      </div>
      <button
        className="btn"
        type="button"
        style={{ marginTop: 18 }}
        disabled={built.length === 0}
        onClick={() => {
          const scored = scoreWordBank(
            built.map((at) => bank[at] ?? ''),
            accepted,
          );
          onDone({
            kind: 'answered',
            outcome: scored.outcome,
            score: scored.outcome === 'correct' ? 1 : 0,
            correction:
              scored.outcome === 'correct' ? null : (accepted[0]?.join(' ') ?? null),
          });
        }}
      >
        Check it
      </button>
    </div>
  );
}

// --- typing ----------------------------------------------------------------

/**
 * Type the answer ([D-149](../../../docs/DECISIONS.md#d-149)).
 *
 * `type_answer`, `dictation` in its typed mode, and a `fill_blank` with no
 * options are this widget. Scored by `scoreTyped`, whose rules are the ones a
 * child would call fair: a missed, doubled or swapped letter, or a finger one
 * key over, is a slip and is accepted — with the spelling shown as a gift — and
 * a different word is not.
 *
 * **The device's own help is switched off.** Autocorrect, autocapitalise and
 * the spell-check squiggle would all change or judge what the child typed
 * before we see it; the forgiveness is ours, and it is the same on every
 * device. Enter checks the answer, because that is what a keyboard's return
 * key means to a child.
 */
function TypeIt({
  prompt,
  shown,
  hear,
  imageRef,
  accepted,
  maxEditDistance,
  onDone,
}: {
  readonly prompt: string;
  /** Written above the field: the question, or a sentence with its gap. */
  readonly shown?: React.ReactNode;
  /** A sound to write down, for dictation. Heard, never shown. */
  readonly hear?: { readonly audioRef: string; readonly text: string } | undefined;
  readonly imageRef?: string | undefined;
  readonly accepted: readonly string[];
  readonly maxEditDistance?: number | undefined;
  readonly onDone: (verdict: Verdict) => void;
}) {
  const [typed, setTyped] = useState('');

  const check = (): void => {
    if (typed.trim() === '') return;
    const scored = scoreTyped(typed, {
      accepted,
      ...(maxEditDistance === undefined ? {} : { maxEditDistance }),
    });
    onDone(typedVerdict(scored, accepted));
  };

  return (
    <div className="pad" style={{ paddingTop: 18 }}>
      <p className="kicker">
        <Tappable text={prompt} />
      </p>
      {shown !== undefined && (
        <div
          className="card center"
          style={{ marginTop: 10, padding: 20, fontSize: '1.25rem', fontWeight: 800 }}
        >
          {shown}
        </div>
      )}
      {imageRef !== undefined && (
        <img
          src={assetUrl(imageRef)}
          alt=""
          style={{
            display: 'block',
            width: 140,
            margin: '12px auto 0',
            borderRadius: 14,
          }}
        />
      )}
      {hear !== undefined && (
        <HearIt audioRef={hear.audioRef} text={hear.text} autoPlay />
      )}
      <form
        style={{ marginTop: 18 }}
        onSubmit={(event) => {
          event.preventDefault();
          check();
        }}
      >
        <input
          className="typein"
          type="text"
          aria-label="Your answer"
          lang={spokenLanguage()}
          value={typed}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          onChange={(event) => {
            setTyped(event.target.value);
          }}
        />
        <button
          className="btn"
          type="submit"
          style={{ marginTop: 18 }}
          disabled={typed.trim() === ''}
        >
          Check it
        </button>
      </form>
    </div>
  );
}

/**
 * What a typed answer is worth to the lesson.
 *
 * A near-miss is `correct` with its spelling as the correction; the feedback
 * sheet shows that in green as *"It's spelled …"*. A miss shows the first
 * accepted answer, as every other exercise does.
 */
export function typedVerdict(
  scored: ReturnType<typeof scoreTyped>,
  accepted: readonly string[],
): Verdict {
  return scored.outcome === 'correct'
    ? {
        kind: 'answered',
        outcome: 'correct',
        score: 1,
        // The authored spelling, not the folded one scoring compares: a child
        // should see "Please write back soon", capital and all.
        correction: scored.exact
          ? null
          : (accepted.find((one) => normalise(one) === scored.matched) ??
            scored.correction),
      }
    : {
        kind: 'answered',
        outcome: 'incorrect',
        score: 0,
        correction: accepted[0] ?? null,
      };
}

/**
 * A sentence with one word missing (`fill_blank`).
 *
 * With options the child taps one; without, they type it. The sentence is
 * written with a line where the word goes — the gap is a place to write, not a
 * box to guess into.
 */
function FillBlank({
  exerciseId,
  sentence,
  blankIndex,
  options,
  accepted,
  onDone,
}: {
  readonly exerciseId: string;
  readonly sentence: string;
  readonly blankIndex: number;
  readonly options: readonly string[] | undefined;
  readonly accepted: readonly string[];
  readonly onDone: (verdict: Verdict) => void;
}) {
  const words = sentence.split(/\s+/);
  const before = words.slice(0, blankIndex).join(' ');
  // The gap takes the word, not its punctuation: "Best ___, Noah", not
  // "Best ___ Noah".
  const kept = /[^\p{L}\p{N}']+$/u.exec(words[blankIndex] ?? '')?.[0] ?? '';
  const after =
    `${kept}${kept === '' ? '' : ' '}${words.slice(blankIndex + 1).join(' ')}`.trim();
  const shown = (
    <div>
      {before !== '' && <Tappable text={before} />}
      {/* An image of a gap, as far as a screen reader is concerned: a label on
          a bare span is not allowed, and "missing word" is what is there. */}
      <span className="blank" role="img" aria-label="missing word">
        &nbsp;
      </span>
      {after !== '' && <Tappable text={after} />}
    </div>
  );
  const order = useMemo(
    () => (options === undefined ? [] : shuffled(options, exerciseId)),
    [options, exerciseId],
  );

  if (options === undefined || options.length === 0) {
    return (
      <TypeIt
        prompt="Write the missing word"
        shown={shown}
        accepted={accepted}
        onDone={onDone}
      />
    );
  }

  const right = new Set(accepted.map(normalise));
  return (
    <div className="pad" style={{ paddingTop: 18 }}>
      <p className="kicker">
        <Tappable text="Which word is missing?" />
      </p>
      <div
        className="card center"
        style={{ marginTop: 10, padding: 20, fontSize: '1.25rem', fontWeight: 800 }}
      >
        {shown}
      </div>
      <div className="tiles" style={{ marginTop: 16 }}>
        {order.map((option) => (
          <button
            key={option}
            className="tile"
            type="button"
            onClick={() => {
              const ok = right.has(normalise(option));
              onDone({
                kind: 'answered',
                outcome: ok ? 'correct' : 'incorrect',
                score: ok ? 1 : 0,
                correction: ok ? null : (accepted[0] ?? null),
              });
            }}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

// --- a paragraph with words missing -----------------------------------------

/** `Pip: I like it.` is Pip saying it; a line with no name is just a line. */
const SPEAKER = /^([A-Z][A-Za-z]*(?: [A-Z][A-Za-z]*)?):\s+(.*)$/;

/**
 * A few sentences with words missing (`gap_text`,
 * [D-184](../../../docs/DECISIONS.md#d-184)).
 *
 * Two ways to fill it, one look. **Choose**: the first empty gap is waiting
 * (sky blue); tapping a word in the bank puts it there and moves on, and tapping
 * a filled gap takes the word back out. **Type**: each gap is a box that starts
 * with the first letters the book allows, and the child writes the rest. Either
 * way nothing is marked until *Check*, and then every gap says how it went, with
 * the answer beside a wrong one — the whole paragraph is the feedback.
 */
function GapText({
  exerciseId,
  mode,
  lines,
  hintLetters,
  distractors,
  onDone,
}: {
  readonly exerciseId: string;
  readonly mode: 'choose' | 'type';
  readonly lines: readonly string[];
  readonly hintLetters: number;
  readonly distractors: readonly string[];
  readonly onDone: (verdict: Verdict) => void;
}) {
  const gaps = useMemo(() => gapsIn(lines), [lines]);
  // Each word in the bank is one tile, even when two gaps want the same word.
  const bank = useMemo(
    () =>
      shuffled(
        [...gaps.map((gap) => gap.answer), ...distractors].map((word, at) => ({
          word,
          at,
        })),
        exerciseId,
      ),
    [gaps, distractors, exerciseId],
  );
  /** For choose: which bank tile is in each gap. For type: what was typed. */
  const [chosen, setChosen] = useState<readonly (number | null)[]>(() =>
    gaps.map(() => null),
  );
  const [typed, setTyped] = useState<readonly string[]>(() => gaps.map(() => ''));
  const [active, setActive] = useState(0);
  const [marked, setMarked] = useState<GapsMarked | null>(null);

  const wordIn = (gap: number): string => {
    if (mode === 'type') {
      const answer = gaps[gap]?.answer ?? '';
      return `${hintFor(answer, hintLetters)}${typed[gap] ?? ''}`;
    }
    const tile = chosen[gap];
    return tile === null || tile === undefined
      ? ''
      : (bank.find((one) => one.at === tile)?.word ?? '');
  };
  const full =
    mode === 'type'
      ? typed.every((one) => one.trim() !== '')
      : chosen.every((one) => one !== null);

  const check = (): void => {
    if (!full || marked !== null) return;
    const result = markGaps(
      gaps,
      gaps.map((_, at) => wordIn(at)),
      mode,
    );
    setMarked(result);
    const fixes = result.marks.flatMap((mark) =>
      mark.correction === null ? [] : [mark.correction],
    );
    onDone({
      kind: 'answered',
      outcome: result.outcome,
      score: result.score,
      correction: fixes.length === 0 ? null : fixes.join(', '),
    });
  };

  const put = (tile: number): void => {
    if (marked !== null) return;
    const next = [...chosen];
    next[active] = tile;
    setChosen(next);
    // On to the next gap still waiting, looking forward first.
    const waiting = [
      ...next.map((_, at) => at).slice(active + 1),
      ...next.map((_, at) => at).slice(0, active),
    ].find((at) => next[at] === null);
    if (waiting !== undefined) setActive(waiting);
  };

  const tapGap = (gap: number): void => {
    if (marked !== null) return;
    if (chosen[gap] !== null) {
      const next = [...chosen];
      next[gap] = null;
      setChosen(next);
    }
    setActive(gap);
  };

  const used = new Set(chosen.filter((one): one is number => one !== null));
  let gapAt = 0;

  return (
    <div className="pad" style={{ paddingTop: 18 }}>
      <p className="kicker">
        <Tappable
          text={
            mode === 'type' ? 'Write the missing words' : 'Fill in the missing words'
          }
        />
      </p>
      <div className="card para" style={{ marginTop: 10 }}>
        {lines.map((line, row) => {
          const said = SPEAKER.exec(line);
          const body = said?.[2] ?? line;
          const pieces = parseGaps(body, gapAt);
          gapAt += pieces.filter((piece) => piece.kind === 'gap').length;
          return (
            <p key={row}>
              {said !== null && <span className="para-who">{said[1]}: </span>}
              {pieces.map((piece, at) => {
                if (piece.kind === 'text')
                  return <Tappable key={at} text={piece.text} />;
                const gap = piece.index;
                const mark = marked?.marks[gap];
                const state =
                  mark === undefined
                    ? gap === active
                      ? ' on'
                      : ''
                    : mark.right
                      ? ' right'
                      : ' wrong';
                const label = `Missing word ${String(gap + 1)}`;
                if (mode === 'type') {
                  const hint = hintFor(piece.answer, hintLetters);
                  return (
                    <span key={at} className={`gap${state}`}>
                      {hint}
                      <input
                        aria-label={
                          hint === '' ? label : `${label}, starts with ${hint}`
                        }
                        value={typed[gap] ?? ''}
                        readOnly={marked !== null}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        size={Math.max(3, piece.answer.length - hint.length)}
                        onFocus={() => {
                          setActive(gap);
                        }}
                        onChange={(event) => {
                          const next = [...typed];
                          next[gap] = event.target.value;
                          setTyped(next);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') check();
                        }}
                      />
                      {mark?.correction != null && (
                        <span className="gap-fix"> {mark.correction}</span>
                      )}
                    </span>
                  );
                }
                const word = wordIn(gap);
                return (
                  <button
                    key={at}
                    type="button"
                    className={`gap${state}`}
                    aria-label={word === '' ? `${label}, empty` : `${label}: ${word}`}
                    aria-pressed={gap === active && marked === null}
                    onClick={() => {
                      tapGap(gap);
                    }}
                  >
                    {word === '' ? '\u00a0' : word}
                    {mark?.correction != null && (
                      <span className="gap-fix"> {mark.correction}</span>
                    )}
                  </button>
                );
              })}
            </p>
          );
        })}
      </div>
      {mode === 'choose' && (
        <div className="tiles" style={{ marginTop: 16 }}>
          {bank.map((one) => (
            <button
              key={one.at}
              type="button"
              className={`tile${used.has(one.at) ? ' used' : ''}`}
              disabled={used.has(one.at) || marked !== null}
              onClick={() => {
                put(one.at);
              }}
            >
              {one.word}
            </button>
          ))}
        </div>
      )}
      {marked === null && (
        <button
          className="btn"
          type="button"
          style={{ marginTop: 18 }}
          disabled={!full}
          onClick={check}
        >
          Check it
        </button>
      )}
    </div>
  );
}

// --- saying it out loud, unscored -------------------------------------------

/**
 * `shadow`: listen, say it if you want to, and move on with full credit.
 *
 * What the mic fallback means by name ([Accessibility](../../../docs/ACCESSIBILITY.md)):
 * the microphone is optional throughout, so a child who has not granted it — or
 * who is on a bus — is never marked down for it. There is nobody to hear them,
 * and that is the design, not a gap.
 */
/**
 * Say it out loud.
 *
 * Two paths, and the one without a microphone is the original and stays
 * untouched: a child taps **I said it** and is taken at their word. That is not
 * a consolation prize — a refused permission, a shared room, a classroom and a
 * child who simply does not want to are all ordinary, and none of them should
 * cost anything ([D-004](../../../docs/DECISIONS.md#d-004)).
 *
 * With a microphone that can listen *without sending the audio anywhere*
 * ([`speech.ts`](./speech.ts)), the child can instead tap **Listen to me** and
 * the device scores what it heard. What changes is only what Sneakers practises
 * next: speaking never gates progress
 * ([D-009](../../../docs/DECISIONS.md#d-009)), and there is no wrong answer on
 * this screen — the worst outcome is `partial`, and what a child *sees* is
 * encouragement either way.
 */
function SayIt({
  text,
  audioRef,
  accepted,
  targetPhonemes,
  tolerance,
  listener,
  onDone,
}: {
  readonly text: string;
  /** The model to imitate. Without one there is nothing to shadow. */
  readonly audioRef: string;
  /** What would count as having said it. */
  readonly accepted: readonly string[];
  readonly targetPhonemes: readonly string[];
  readonly tolerance: number;
  /** A way to listen, or null when this device has none it may use. */
  readonly listener: SpeechRecognizer | null;
  readonly onDone: (verdict: Verdict) => void;
}) {
  const [listening, setListening] = useState(false);
  /** What Sneakers says afterwards. Never a failure, so never red. */
  const [said, setSaid] = useState<string | null>(null);

  // Let go of the microphone the moment this exercise goes away, rather than
  // when a promise happens to settle. A recogniser still running behind the
  // next question is a light a child can see and a permission they did not
  // agree to keep granting.
  useEffect(() => () => listener?.stop(), [listener]);

  const listen = (): void => {
    if (listener === null || listening) return;
    setListening(true);
    listener
      .listen()
      .then((heard) => {
        const result = scoreSpoken({
          heard,
          accepted,
          targetPhonemes,
          tolerance,
        });
        setListening(false);
        setSaid(
          result.heardNothing
            ? 'I did not catch that — have another go, or tap “I said it”.'
            : pick(CORRECT),
        );
        // Straight on when it landed. When it did not, the line above is shown
        // and the child decides: try again, or move on with "I said it". Being
        // made to repeat something until a recogniser agrees is the experience
        // this app exists to not be.
        if (!result.heardNothing) {
          onDone({
            kind: 'answered',
            outcome: result.outcome,
            score: result.score,
            correction: null,
            phonemes: result.phonemes,
          });
        }
      })
      .catch(() => {
        // A refused microphone, or a recogniser that broke. Either way this
        // child is now a child without a microphone, which is a state that
        // already works.
        setListening(false);
        setSaid('The microphone is not working. Tap “I said it” instead.');
      });
  };

  return (
    <div className="pad" style={{ paddingTop: 18 }}>
      <p className="kicker">
        <Tappable text="Say it out loud" />
      </p>
      <div className="card center" style={{ marginTop: 12, padding: 22 }}>
        <div style={{ fontSize: '1.4375rem', fontWeight: 800 }}>
          <Tappable text={text} />
        </div>
        <HearIt audioRef={audioRef} text={text} />
      </div>

      {listener !== null && (
        <button
          className="btn"
          type="button"
          style={{ marginTop: 18 }}
          disabled={listening}
          onClick={listen}
        >
          {listening ? 'Listening…' : '🎤 Listen to me'}
        </button>
      )}

      {said !== null && (
        <p className="tiny" role="status" style={{ marginTop: 12 }}>
          {said}
        </p>
      )}

      <button
        className={listener === null ? 'btn' : 'btn ghost'}
        type="button"
        style={{ marginTop: listener === null ? 18 : 9 }}
        onClick={() => {
          onDone(right());
        }}
      >
        I said it
      </button>
    </div>
  );
}

// --- shown, not asked -------------------------------------------------------

/**
 * An exercise this build can show but cannot ask.
 *
 * `sound_focus` is two words that differ by one sound, and `audio_word_match`
 * pairs sounds with words: both are about hearing, and their authored fallbacks
 * substitute artwork or the mascot's mouth, neither of which exists yet. So the
 * words are shown and nothing is recorded — a child who was not asked a question
 * has not gone wrong.
 */
function JustLook({
  words,
  note,
  onDone,
}: {
  /** Each word, and the recording that says it if the course has one. */
  readonly words: readonly {
    readonly text: string;
    readonly audioRef: string | null;
  }[];
  readonly note: string;
  readonly onDone: (verdict: Verdict) => void;
}) {
  return (
    <div className="pad" style={{ paddingTop: 18 }}>
      <p className="kicker">
        <Tappable text="Have a look" />
      </p>
      <div className="card" style={{ marginTop: 12 }}>
        <div className="tiles" style={{ justifyContent: 'flex-start' }}>
          {words.map((word) => (
            // Tappable, because hearing a minimal pair side by side is the whole
            // exercise. It is still not scored: listening is not answering.
            <button
              className="chip"
              type="button"
              key={word.text}
              onClick={() => {
                void say(word.audioRef, word.text);
              }}
            >
              <span aria-hidden="true">🔊</span> {word.text}
            </button>
          ))}
        </div>
        <p className="tiny" style={{ marginTop: 10 }}>
          {note}
        </p>
      </div>
      <button
        className="btn ghost"
        type="button"
        style={{ marginTop: 18 }}
        onClick={() => {
          onDone({ kind: 'seen' });
        }}
      >
        Next
      </button>
    </div>
  );
}

// --- the dispatcher ---------------------------------------------------------

/** Pick the widget for one exercise, or say honestly that it cannot be shown. */
function widgetFor(
  exerciseId: string,
  payload: Payload,
  onDone: (verdict: Verdict) => void,
  /** A way to listen, or null on a device that has none it may use. */
  listener: SpeechRecognizer | null,
): React.ReactElement {
  switch (payload.type) {
    case 'listen_choose':
    case 'meaning_choose':
    case 'picture_choice': {
      const options: Choice[] = payload.options.map((option, at) => ({
        key: keyOf(option, at),
        label: option.label ?? option.id ?? `Answer ${String(at + 1)}`,
        imageRef: 'imageRef' in option ? option.imageRef : undefined,
        correct: option.correct,
      }));
      // `listen_choose` has no written form of its own: the question *is* the
      // sound. Showing the correct option's label would answer it, so nothing is
      // shown and the child taps to hear instead — which is the exercise.
      const written = payload.type === 'listen_choose' ? null : payload.l2Text;
      return (
        <Choose
          exerciseId={exerciseId}
          prompt={
            payload.type === 'listen_choose' ? payload.prompt : 'What does it mean?'
          }
          written={written}
          audioRef={payload.audioRef}
          options={options}
          onDone={onDone}
        />
      );
    }

    case 'match_pairs':
      return (
        <MatchPairs exerciseId={exerciseId} pairs={payload.pairs} onDone={onDone} />
      );

    case 'word_bank_order':
      return (
        <PutInOrder
          exerciseId={exerciseId}
          prompt="Build the sentence"
          tiles={[...payload.tiles, ...payload.decoys]}
          accepted={[
            payload.tiles,
            ...payload.acceptedAlternatives.map((line) => line.split(/\s+/)),
          ]}
          // Said out loud on arrival, with buttons to hear it again and
          // slower. A child building a sentence they have never heard is
          // guessing at word order; hearing it is the whole help. Every word
          // bank has had this recording; it was simply never played.
          hear={{ audioRef: payload.audioRef, text: payload.l2Text }}
          onDone={onDone}
        />
      );

    case 'order_dialogue': {
      const inOrder = [...payload.lines].sort((a, b) => a.position - b.position);
      return (
        <PutInOrder
          exerciseId={exerciseId}
          prompt="Put the conversation in order"
          tiles={payload.lines.map((line) => line.text)}
          accepted={[inOrder.map((line) => line.text)]}
          onDone={onDone}
        />
      );
    }

    case 'speak_repeat':
      return (
        <SayIt
          text={payload.l2Text}
          audioRef={payload.audioRef}
          accepted={[payload.l2Text]}
          targetPhonemes={payload.targetPhonemes}
          tolerance={payload.toleranceBand}
          listener={listener}
          onDone={onDone}
        />
      );

    case 'speak_answer':
      // The question is asked in writing and the answer is a spoken English
      // phrase, so what gets spoken is the first accepted answer — the model the
      // child imitates — rather than the prompt.
      return (
        <SayIt
          text={payload.acceptedAnswers[0] ?? payload.questionText}
          audioRef={payload.audioRef}
          accepted={payload.acceptedAnswers}
          // A free answer names no sounds to practise, so it teaches the gap
          // map nothing — which is honest rather than a gap in the wiring.
          targetPhonemes={[]}
          tolerance={SPOKEN_ANSWER_TOLERANCE}
          listener={listener}
          onDone={onDone}
        />
      );

    case 'sound_focus':
      // Two words that differ by one sound, each with its own recording. Tapping
      // them in turn is the exercise; it is exposure, so it is never scored.
      return (
        <JustLook
          words={payload.pair.map((text, at) => ({
            text,
            audioRef: payload.audioRefs[at] ?? null,
          }))}
          note={`Hear the difference: ${payload.contrast}`}
          onDone={onDone}
        />
      );

    case 'audio_word_match':
      // The authored no-audio fallback for this one asks for pictures nobody has
      // drawn, so it stays exposure rather than becoming a guess between two
      // silent tiles ([D-063](../../../docs/DECISIONS.md#d-063)).
      return (
        <JustLook
          words={payload.pairs.map((pair) => ({
            text: pair.l2Text,
            audioRef: pair.audioRef,
          }))}
          note="Listen to the words, and notice how they differ."
          onDone={onDone}
        />
      );

    // Authored types this build has no widget for. Listed rather than left to a
    // `default`, so adding a seventeenth exercise type to the vocabulary is a
    // decision made here and not a silent fall-through.
    case 'story':
      return (
        <Story
          exerciseId={exerciseId}
          sceneRef={payload.sceneRef}
          cast={payload.cast}
          lines={payload.lines}
          checks={payload.checks}
          teaches={payload.teaches}
          listener={listener}
          onDone={onDone}
        />
      );

    case 'type_answer':
      return (
        <TypeIt
          prompt={payload.prompt}
          imageRef={payload.imageRef}
          accepted={payload.accepted}
          maxEditDistance={payload.maxEditDistance}
          onDone={onDone}
        />
      );

    case 'dictation': {
      const hear = { audioRef: payload.audioRef, text: payload.l2Text };
      if (payload.mode === 'type') {
        return (
          <TypeIt
            prompt="Listen. Then write what you hear"
            hear={hear}
            accepted={[payload.l2Text]}
            onDone={onDone}
          />
        );
      }
      const words = payload.l2Text.split(/\s+/);
      return (
        <PutInOrder
          exerciseId={exerciseId}
          prompt="Listen. Then build what you hear"
          tiles={words}
          accepted={[words]}
          hear={hear}
          onDone={onDone}
        />
      );
    }

    case 'gap_text':
      return (
        <GapText
          exerciseId={exerciseId}
          mode={payload.mode}
          lines={payload.lines}
          hintLetters={payload.hintLetters}
          distractors={payload.distractors}
          onDone={onDone}
        />
      );

    case 'fill_blank':
      return (
        <FillBlank
          exerciseId={exerciseId}
          sentence={payload.sentenceText}
          blankIndex={payload.blankIndex}
          options={payload.options}
          accepted={payload.accepted}
          onDone={onDone}
        />
      );

    case 'build_sentence':
      return (
        <PutInOrder
          exerciseId={exerciseId}
          prompt="Build a sentence about the picture"
          tiles={[...payload.tiles, ...payload.decoys]}
          accepted={[
            payload.tiles,
            ...payload.acceptedAlternatives.map((line) => line.split(/\s+/)),
          ]}
          imageRef={payload.imageRef}
          onDone={onDone}
        />
      );

    case 'label_scene': {
      // The boxes are computed from the stage's own drawing (D-155). The
      // payload check has already refused a stage nobody drew; this is the
      // case the types cannot rule out, answered with the words rather than a
      // blank picture a child cannot answer.
      const where = scene(payload.sceneRef);
      if (where === undefined) {
        return (
          <JustLook
            words={payload.targets.map((t) => ({ text: t.text, audioRef: t.audioRef }))}
            note="Listen to each word."
            onDone={onDone}
          />
        );
      }
      return (
        <LabelScene
          exerciseId={exerciseId}
          stage={where}
          targets={payload.targets}
          onDone={onDone}
        />
      );
    }
  }
}

/**
 * A story: a short scene, one line at a time, with comprehension checks at the
 * moments they matter ([Stories](../../../docs/STORIES.md),
 * [D-028](../../../docs/DECISIONS.md#d-028)).
 *
 * **A check never blocks the scene.** Getting one wrong replays the line, says
 * the answer warmly, and carries on — there is no branch in here that keeps a
 * child from finding out what happens, because that would turn the best part of
 * the app into the worst.
 *
 * The checks are rendered by `widgetFor`, the same dispatcher the rest of the
 * lesson uses: a check *is* an exercise, so a story needs no second set of
 * widgets to fall behind the first.
 */
function Story({
  exerciseId,
  sceneRef,
  cast,
  lines,
  checks,
  teaches,
  listener,
  onDone,
}: {
  readonly exerciseId: string;
  /** Which stage, or undefined for a story nobody gave one. */
  readonly sceneRef: string | undefined;
  readonly cast: readonly string[];
  readonly lines: readonly {
    readonly speaker: string;
    readonly text: string;
    readonly audioRef: string;
  }[];
  readonly checks: readonly { afterLine: number; check: Payload }[];
  readonly teaches: readonly string[];
  readonly listener: SpeechRecognizer | null;
  readonly onDone: (verdict: Verdict) => void;
}) {
  const [shown, setShown] = useState(1);
  const [answered, setAnswered] = useState<ReadonlySet<number>>(new Set());
  /** The line to say again, and the answer to give away. Both mean "got it wrong". */
  const [reveal, setReveal] = useState<{ line: number; answer: string | null } | null>(
    null,
  );

  const step = storyStep(
    lines.map((line, at) => ({ n: at + 1, ...line })),
    checks,
    shown,
    answered,
  );

  const done = (check: { afterLine: number; index: number }) => (verdict: Verdict) => {
    const right = verdict.kind === 'answered' && verdict.outcome === 'correct';
    const what = afterCheck(right, check.afterLine);
    setReveal(
      what.tellThem
        ? {
            line: what.replayLine ?? check.afterLine,
            answer: verdict.kind === 'answered' ? verdict.correction : null,
          }
        : null,
    );
    // Answered either way. This is the rule, in one line.
    setAnswered((was) => new Set([...was, check.index]));
  };

  if (step.ended) {
    return (
      <div className="pad" style={{ paddingTop: 18 }}>
        <div className="card">
          <div style={{ fontWeight: 800 }}>You followed the whole thing!</div>
          {teaches.length > 0 && (
            <>
              <p className="tiny" style={{ marginTop: 6 }}>
                You can say:
              </p>
              <ul className="tiny" style={{ marginTop: 4, paddingLeft: 18 }}>
                {teaches.map((what) => (
                  <li key={what}>
                    <Tappable text={what} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <button
          className="btn"
          type="button"
          style={{ marginTop: 14 }}
          onClick={() => {
            // Seen, not assessed. The checks inside were the questions; the
            // story itself is not one more.
            onDone({ kind: 'seen' });
          }}
        >
          Next
        </button>
      </div>
    );
  }

  return (
    <div className="pad" style={{ paddingTop: 14 }}>
      {/* Where we are, and who is in it. Draws nothing when there is no stage. */}
      <Stage sceneRef={sceneRef} cast={cast} />

      {/*
        Everything played so far, scrolling. A child follows a conversation they
        only half caught by looking back at it.
      */}
      {step.played.map((line, at) => (
        <div
          key={`${exerciseId}-${String(at)}`}
          className="card"
          style={{
            marginTop: at === 0 ? 0 : 8,
            marginLeft: line.speaker === 'sneakers' ? 0 : 24,
            marginRight: line.speaker === 'sneakers' ? 24 : 0,
            background: line.speaker === 'sneakers' ? '#FFFFFF' : '#EAF4FF',
          }}
        >
          <div
            className="tiny"
            style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Portrait speaker={line.speaker} />
            {NAMES[line.speaker] ?? line.speaker}
          </div>
          <div style={{ marginTop: 4 }}>
            <Tappable text={line.text} />
          </div>
          {/* Replay any line by tapping it. Unlimited, always. */}
          <HearIt audioRef={line.audioRef} text={line.text} />
        </div>
      ))}

      {/*
        The warm reveal. Shown *with* the line in front of them, which is why a
        wrong answer replays it rather than only saying the words.
      */}
      {reveal !== null && (
        <div
          className="card"
          role="status"
          style={{ marginTop: 10, background: '#FDF6E0' }}
        >
          <div style={{ fontWeight: 800 }}>Nearly! Listen again.</div>
          {reveal.answer !== null && (
            <p className="tiny" style={{ marginTop: 4 }}>
              It was: <Tappable text={reveal.answer} />
            </p>
          )}
        </div>
      )}

      {step.asking !== null ? (
        <div style={{ marginTop: 10 }}>
          {widgetFor(
            `${exerciseId}-check-${String(step.asking.index)}`,
            step.asking.check,
            done(step.asking),
            listener,
          )}
        </div>
      ) : (
        <button
          className="btn"
          type="button"
          style={{ marginTop: 14 }}
          onClick={() => {
            setReveal(null);
            setShown((was) => was + 1);
          }}
        >
          {shown >= lines.length ? 'The end' : 'Then…'}
        </button>
      )}
    </div>
  );
}

/** What each character is called on screen. An id nobody named shows as itself. */
const NAMES: Readonly<Record<string, string>> = {
  sneakers: 'Sneakers',
  nib: 'Nib',
  mrs_otter: 'Mrs Otter',
  bruno: 'Bruno',
  pip: 'Pip',
};

/**
 * An exercise to show, which is less than a lesson's queue entry.
 *
 * Narrower than `Queued` on purpose: the only two fields any widget reads are
 * the id and the payload. The placement test shows the same exercises without
 * a `source` or a time estimate — it is not a lesson and has no queue — and
 * asking it to invent those would be asking it to lie in two fields to satisfy
 * a type.
 */
export interface Showable {
  readonly exerciseId: string;
  /**
   * Optional, because an older server may not send it
   * ([D-066](../../../docs/DECISIONS.md#d-066)) — the parse below turns that
   * into "this exercise is broken" rather than a white screen.
   */
  readonly payload?: unknown;
}

export function ExerciseView({
  exercise,
  listener = null,
  onDone,
}: {
  readonly exercise: Showable;
  /**
   * A way to listen, when the device has one the audio may stay on.
   *
   * Defaults to null, which is the state every screen but the lesson runner is
   * in — the placement test does not ask a stranger to speak, and a widget with
   * no listener is exactly the widget this app shipped before.
   */
  readonly listener?: SpeechRecognizer | null;
  readonly onDone: (verdict: Verdict) => void;
}) {
  const parsed = ExercisePayloadSchema.safeParse(exercise.payload);
  if (!parsed.success) {
    // Content the linter would have refused. It should never reach a child, and
    // if it does the lesson carries on rather than ending on a broken screen.
    return <JustLook words={[]} note="This exercise is broken." onDone={onDone} />;
  }
  // Keyed by exercise so each one starts with its own state: without this, the
  // tiles a child tapped on question three are still tapped on question four.
  return (
    <div key={exercise.exerciseId}>
      {widgetFor(exercise.exerciseId, parsed.data, onDone, listener)}
    </div>
  );
}
